// Replays at the Dance Hall: the hall keeps its last twenty performances (the
// server's :past, newest first, with how everyone there looked and the visitors
// who came). When nothing is on, a ticket clipped to the marquee replays the
// last one, and the playbill beside it lists all ten. A replay plays on the
// stage like the real thing (the same moves from its beats, the original
// dancers in their colours, those who watched back round the floor, the
// visitors by the door), under a marquee that says Replay. The hall's own
// plazmi step aside meanwhile, and a real performance cuts in at once.
import * as THREE from 'three';
import { at, finishShadows } from './kit.js';
import { visitor as toyFigure } from './chars.js';
import { Fx } from './fx.js';
import type * as Fu from './furnish.js';
import { Troupe, BEAT_MS, beatsOf, type PlaceLive, type Present, type Showing, type TroupeOpts } from './place.js';

type Look = { slug: string; name: string; emoji?: string; color: string; glow: string };
export type Past = Showing & { watching?: string[]; cast?: Look[]; visitors?: { id: string; name: string; look?: Record<string, string> }[] };

const el = (tag: string, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };

// when, in world time (UTC), as a person would say it: "this morning, 05:41"
const PART = (h: number) => (h < 5 ? 'in the small hours' : h < 12 ? 'morning' : h < 17 ? 'afternoon' : h < 21 ? 'evening' : 'night');
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export function whenWords(t: number, now: number) {
  const d = new Date(t), hm = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  const days = Math.floor(now / 864e5) - Math.floor(t / 864e5), p = PART(d.getUTCHours());
  const day = days <= 0 ? (p === 'night' ? 'tonight' : p.startsWith('in') ? `${p}` : `this ${p}`)
    : days === 1 ? (p === 'night' ? 'last night' : p.startsWith('in') ? `yesterday, ${p}` : `yesterday ${p}`)
      : days < 7 ? `${DAYS[d.getUTCDay()]} ${p.startsWith('in') ? 'night' : p}`
        : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${day}, ${hm}`;
}

// (P86) when it was danced, in the visitor's own time as the chat shows it: "Sat, Oct 10" and "18:26"
export function danceWhen(t: number) {
  const d = new Date(t);
  return {
    date: d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }),
    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
    iso: d.toISOString(),
  };
}

// (P86) how long it runs on the floor, as a replay plays it (place.ts startShow): the lead-in,
// two to six times through (half a minute or so), the bows. "40 s", "1 min 10 s"
export function danceLength(p: Showing) {
  const beats = p.beats?.length ? p.beats : [{ move: 'dance' }];
  const loop = beats.reduce((a, b) => a + beatsOf(b as any), 0);
  const loops = Math.max(2, Math.min(6, Math.ceil(30000 / (loop * BEAT_MS))));
  const s = Math.round(((12 + loops * loop + 4) * BEAT_MS) / 5000) * 5;
  return s < 90 ? `${s} s` : `${Math.floor(s / 60)} min${s % 60 ? ` ${s % 60} s` : ''}`;
}

// "Ashlar, Pip and Mira", "Ashlar, Pip and 4 more"
export function someNames(ns: string[], max = 3) {
  if (ns.length <= max) return ns.length < 2 ? ns.join('') : `${ns.slice(0, -1).join(', ')} and ${ns[ns.length - 1]}`;
  return `${ns.slice(0, max - 1).join(', ')} and ${ns.length - max + 1} more`;
}

const CSS = `
.mq-replay { position: absolute; left: 100%; margin-left: -34px; top: -22px; display: flex; flex-direction: column; align-items: flex-end; gap: 5px; pointer-events: none; text-align: left; }
.mq-replay[hidden] { display: none; }
.rp-ticket { pointer-events: auto; display: flex; align-items: stretch; cursor: pointer; font: inherit; border: none; padding: 0; background: none; color: #3a2a20;
  transform: rotate(4deg); filter: drop-shadow(0 7px 8px rgba(20,10,20,.45)); transition: transform .2s ease-out; animation: rp-in .7s ease-out; }
.rp-ticket:hover { transform: rotate(1deg) translateY(-2px); }
.rp-ticket .stub { display: grid; place-items: center; width: 34px; background: linear-gradient(180deg, #d0614a, #b84a35); color: #fff3df; font-size: 14px;
  border-radius: 6px 0 0 6px; border-right: 2px dashed rgba(251,245,232,.85); }
.rp-ticket .body { display: flex; flex-direction: column; gap: 1px; padding: 6px 13px 7px 10px; border-radius: 0 6px 6px 0; max-width: 190px;
  background-image: radial-gradient(circle at 0 0, transparent 6px, #fbf5e8 6.5px), radial-gradient(circle at 0 100%, transparent 6px, #fbf5e8 6.5px);
  background-size: 100% 51%; background-position: top, bottom; background-repeat: no-repeat; }
.rp-ticket small { font-size: 10.5px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; color: #b84a35; }
.rp-ticket b { font-size: 13px; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-shadow: none; letter-spacing: 0; }
.rp-ticket.stop .stub { background: linear-gradient(180deg, #5a6f86, #44566b); }
.rp-ticket.stop small { color: #44566b; }
.rp-tag { pointer-events: auto; cursor: pointer; font: inherit; font-size: 11.5px; font-weight: 800; color: #fbecd2; letter-spacing: .04em;
  background: linear-gradient(180deg, #8a2f45, #6e2238); border: 1.5px solid #3e1020; border-radius: 3px 3px 9px 9px; padding: 3px 10px 4px;
  transform: rotate(-3deg); margin-right: -6px; box-shadow: 0 5px 9px -6px rgba(0,0,0,.6); }
.rp-tag:before { content: ""; display: inline-block; width: 5px; height: 5px; border-radius: 50%; background: #e9c46a; margin-right: 6px; vertical-align: 2px; }
.rp-tag.on { background: linear-gradient(180deg, #a3405a, #8a2f45); }
@keyframes rp-in { from { transform: translateY(14px) rotate(10deg); opacity: 0; } to { transform: rotate(4deg); opacity: 1; } }
.marquee.replay { box-shadow: 0 0 0 4px #9aa9bb, 0 0 0 7px #4e5d70, 0 14px 30px -12px rgba(0,0,0,.7); }
.marquee.replay small { color: #9fd3ff; }
.marquee.replay .bulbs { border-color: #cfe6ff; animation-duration: 1.6s; }
#ui.replaying .hello button.cheer { display: none; }
/* the playbill: the last twenty dances, a compact list in the dock's "What's on" (P86). The
   board on the stage (hallboard.ts) is the one in the world; this is the same list, one tap away */
.playbill { color: var(--pz-ink, #3a2a20); }
.playbill[hidden] { display: none; }
.playbill > header, .playbill > footer { display: none; }
.playbill ol { list-style: none; margin: 0; padding: 0; }
.playbill li + li { border-top: 1px dashed var(--pz-rule, #e2d4ba); }
.playbill li button { all: unset; box-sizing: border-box; width: 100%; display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: 10px; row-gap: 1px;
  align-items: center; padding: 7px 2px; cursor: pointer; border-radius: 3px; }
.playbill li button:hover { background: var(--pz-paper-2, #f5ecd9); }
.playbill li button:focus-visible { outline: 2px solid var(--pz-brass, #c9a24a); outline-offset: 2px; }
.playbill .n { display: none; }
.playbill .t { grid-column: 1; grid-row: 1; font-size: 14px; font-weight: 800; line-height: 1.25; min-width: 0; overflow-wrap: anywhere; }
.playbill .t em { font-style: normal; font-weight: 600; color: var(--pz-ink-2, #6e5a48); }
.playbill .w { grid-column: 1; grid-row: 2; font-size: 12px; line-height: 1.35; color: var(--pz-ink-3, #9a8670); font-variant-numeric: tabular-nums; min-width: 0; }
.playbill .play { grid-column: 2; grid-row: 1 / span 2; font-size: 12.5px; font-weight: 800; color: var(--pz-brass-ink, #4a3712); white-space: nowrap;
  background: linear-gradient(180deg, var(--pz-brass-2, #e3c473), var(--pz-brass, #c9a24a)); border: 1.5px solid var(--pz-ink, #3a2a20); border-radius: 3px; padding: 3px 8px 4px; }
.playbill .pb-who { grid-column: 1 / -1; grid-row: 3; font-size: 12px; line-height: 1.4; color: var(--pz-ink-2, #6e5a48); display: flex; flex-wrap: wrap; align-items: center; column-gap: 9px; min-width: 0; }
.playbill .pb-who:empty { display: none; }
.playbill .pb-who > span { min-width: 0; } .playbill .pb-who > span b { font-weight: 700; color: var(--pz-ink-3, #9a8670); margin-right: 3px; }
.playbill .dots { display: inline-flex; margin-right: 1px; vertical-align: -1px; } .playbill .dots i { width: 10px; height: 10px; border-radius: 50%; margin-right: -3px; box-shadow: 0 0 0 1.5px var(--pz-paper, #fffaf0), inset 0 -2px 0 rgba(0,0,0,.15); }
.playbill .cheers { color: #a8660e; font-weight: 700; }
@media (max-width: 600px) {
  .mq-replay { position: static; flex-direction: row; justify-content: center; align-items: center; margin-top: 8px; gap: 10px; }
  .rp-ticket { transform: none; animation: none; } .rp-ticket:hover { transform: none; }
  .rp-tag { margin: 0; transform: none; }
}
`;

type Fig = { fig: THREE.Object3D };

export class Replays {
  troupe: Troupe | null = null;
  past: Past[] = [];
  showing: Past | null = null;
  private now = Date.now();
  private live = false;
  private box = el('div', 'mq-replay');
  private ticket = el('button', 'rp-ticket') as HTMLButtonElement;
  private tag = el('button', 'rp-tag', 'Playbill') as HTMLButtonElement;
  private bill = el('div', 'playbill');
  private list = el('ol');
  private figs = new THREE.Group();
  private billKey = '';

  constructor(private o: {
    ui: HTMLElement; mq: HTMLElement; billHost?: HTMLElement; scene: THREE.Scene; fx: Fx; floor: Fu.House; opts: TroupeOpts;
    hall: Troupe; people: THREE.Object3D; onChange: () => void; prewarm?: (t: Troupe) => void;
  }) {
    const s = document.createElement('style'); s.textContent = CSS; document.head.appendChild(s);
    this.ticket.type = this.tag.type = 'button';
    this.ticket.innerHTML = '<span class="stub">▶</span><span class="body"><small></small><b></b></span>';
    this.box.append(this.ticket, this.tag); this.box.hidden = true;
    o.mq.appendChild(this.box);
    const head = el('header'); const x = el('button', '', '×') as HTMLButtonElement; x.type = 'button'; x.title = 'Fold the playbill away';
    head.append(el('b', '', 'Playbill'), el('span', '', 'the last twenty dances at the hall'), x);
    this.bill.append(head, this.list, el('footer', '', 'Pick one to see it again on the stage. Replays are marked so on the marquee.'));
    this.bill.hidden = true;
    (o.billHost || o.ui).appendChild(this.bill);
    x.addEventListener('click', () => this.fold(true));
    this.tag.addEventListener('click', () => this.fold(!this.bill.hidden));
    this.ticket.addEventListener('click', () => (this.on ? this.stop() : this.play(0)));
    addEventListener('resize', () => this.placeBill());
    o.scene.add(this.figs);
  }

  get on() { return !!this.troupe; }
  // nothing real on, and something to see again
  canReplay() { return !this.live && this.past.length > 0; }

  // a poll's news: the playbill, and whether something real is on (a real show cuts in)
  hear(l: PlaceLive & { past?: Past[] }) {
    this.now = l.now;
    this.past = l.past || [];
    this.live = !!l.showing && l.now < (l.showing.until ?? l.showing.at + 150000) && l.marquee.title === 'Now dancing';
    if (this.live && this.on) this.stop();
    // in the dock, the playbill is there whenever there's something on it
    if (this.o.billHost) this.bill.hidden = !this.past.length;
    this.drawBill();
    this.drawTicket();
  }

  note() { const p = this.showing; if (!p) return ''; const w = danceWhen(p.at); return `A replay is on the stage: ${p.line}, from ${w.date} at ${w.time}. Everyone here now waits in the wings till it's done.`; }

  title() { if (!this.showing) return ''; const w = danceWhen(this.showing.at); return `Replay · ${w.date}, ${w.time}`; }

  play(i: number) {
    const p = this.past[i];
    if (!p) return;
    this.stop(false);
    this.fold(true);
    const now = Date.now() + this.o.hall.offset;
    const cast = p.cast?.length ? p.cast : p.dancers.map((slug) => ({ slug, name: slug, color: '#f2a37b', glow: '#9be8ff' }));
    const present: Present[] = cast.map((c) => ({ ...c, why: 'visit', since: 0 }));
    const t = new Troupe(this.o.floor, this.o.ui, this.o.fx, this.o.opts);
    this.troupe = t; this.showing = p;
    t.hear({ now, place: 'hall', name: 'the Dance Hall', present, showing: null, marquee: { title: 'Replay', line: p.line }, bill: [], suggested: null, cheers: 0, chat: [], visitors: [] });
    t.startShow({ ...p, at: now });
    this.o.scene.add(t.root);
    // the hall's own plazmi and visitors step aside while it plays
    this.o.hall.root.visible = false; this.o.people.visible = false;
    for (const d of this.o.hall.dancers.values()) d.bubble.style.display = 'none'; // (their name tags go with them)
    (p.visitors || []).slice(0, 8).forEach((v, k) => {
      const fig = at(toyFigure((v.look || {}) as any), 14.0 + (k % 3) * 0.9, 0, 11.2 + Math.floor(k / 3) * 0.8, -2.3);
      finishShadows(fig); this.figs.add(fig);
    });
    this.o.ui.classList.add('replaying');
    this.o.mq.classList.add('replay');
    this.o.prewarm?.(t);
    this.drawTicket();
    this.o.onChange();
  }

  stop(tell = true) {
    const t = this.troupe;
    if (!t) return;
    for (const d of t.dancers.values()) { d.tag.remove(); d.bubble.remove(); }
    t.root.removeFromParent();
    this.figs.clear();
    this.troupe = null; this.showing = null;
    this.o.hall.root.visible = true; this.o.people.visible = true;
    this.o.ui.classList.remove('replaying');
    this.o.mq.classList.remove('replay');
    this.drawTicket();
    if (tell) this.o.onChange();
  }

  update(dt: number) {
    const t = this.troupe;
    if (!t) return;
    t.update(dt);
    // the bows are done: back to the hall as it is
    if (t.show?.over) this.stop();
  }

  pin(cam: THREE.Camera, vw: number, vh: number) { this.troupe?.pin(cam, vw, vh); }

  private fold(shut: boolean) {
    if (this.o.billHost) { this.bill.hidden = !this.past.length; return; }
    this.bill.hidden = shut || !this.past.length;
    this.tag.classList.toggle('on', !this.bill.hidden);
    this.placeBill();
  }

  // the playbill stands just above the marquee
  private placeBill() {
    if (this.bill.hidden) return;
    const r = this.o.mq.getBoundingClientRect();
    this.bill.style.bottom = `${Math.round(innerHeight - r.top + 22)}px`;
    this.bill.style.maxHeight = `${Math.max(160, Math.round(r.top - 22 - 70))}px`;
  }

  private drawTicket() {
    const show = this.on || (!this.live && this.past.length > 0);
    this.box.hidden = !show;
    this.tag.hidden = this.on;
    if (!show) { this.fold(true); return; }
    this.ticket.classList.toggle('stop', this.on);
    (this.ticket.querySelector('.stub') as HTMLElement).textContent = this.on ? '■' : '▶';
    (this.ticket.querySelector('small') as HTMLElement).textContent = 'Replay';
    (this.ticket.querySelector('b') as HTMLElement).textContent = this.on ? 'Back to now' : 'the last dance';
    this.ticket.title = this.on ? 'Stop the replay' : `${this.past[0].title}, ${whenWords(this.past[0].at, this.now)}`;
  }

  private drawBill() {
    const k = JSON.stringify([this.past.map((p) => [p.at, p.cheers, p.visitors?.length]), Math.floor(this.now / 36e5)]);
    if (k === this.billKey) return; this.billKey = k;
    this.list.innerHTML = '';
    this.past.forEach((p, i) => {
      const li = el('li'), b = el('button') as HTMLButtonElement; b.type = 'button';
      const t = el('span', 't', cap(p.title));
      const by = p['crew-name'] || someNames(p.dancers.map((s) => p.cast?.find((c) => c.slug === s)?.name || s), 2);
      t.append(' ', el('em', '', `· ${by}`));
      const who = el('span', 'pb-who');
      const names = (slugs: string[]) => slugs.map((s) => p.cast?.find((c) => c.slug === s)?.name || s);
      const part = (label: string, text: string, dots?: string[]) => {
        const s = el('span'); s.append(el('b', '', `${label}:`));
        if (dots?.length) { const d = el('span', 'dots'); for (const c of dots.slice(0, 5)) { const x = el('i'); x.style.background = c; d.append(x); } s.append(d, ' '); }
        s.append(text); who.append(s);
      };
      const colour = (slug: string) => p.cast?.find((c) => c.slug === slug)?.color || '#f2a37b';
      part('danced', someNames(names(p.dancers)), p.dancers.map(colour));
      if (p.watching?.length) part('watching', someNames(names(p.watching)), p.watching.map(colour));
      if (p.visitors?.length) part('visitors', someNames(p.visitors.map((v) => v.name)));
      if (p.cheers) who.append(el('span', 'cheers', `${p.cheers} cheer${p.cheers === 1 ? '' : 's'}`));
      const wh = danceWhen(p.at);
      b.append(el('span', 'n', String(i + 1)), t, el('span', 'w', `${wh.date} · ${wh.time} · ${danceLength(p)}`), el('span', 'play', '▶ Watch'), who);
      b.setAttribute('aria-label', `Watch ${p.title}, danced by ${by} on ${wh.date} at ${wh.time}, again on the floor`);
      b.addEventListener('click', () => this.play(i));
      li.append(b); this.list.append(li);
    });
  }
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
